import React, { forwardRef, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';

export type ValidationState = 'valid' | 'invalid' | 'none';

/**
 * Optional trailing action button(s) for the input.
 * Supports toggle behavior: shows primaryIcon when isPrimaryActive, secondaryIcon otherwise.
 * On click, onToggle is invoked (caller typically flips isPrimaryActive).
 */
export interface InputFieldTrailingAction {
    primaryIcon: React.ComponentType<{ className?: string }>;
    secondaryIcon: React.ComponentType<{ className?: string }>;
    isPrimaryActive: boolean;
    onToggle: () => void;
    /** Accessible label for the primary state (e.g. "Show password") */
    primaryLabel: string;
    /** Accessible label for the secondary state (e.g. "Hide password") */
    secondaryLabel: string;
}

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    labelText?: string;
    fieldRequired?: boolean;
    fieldPlaceholder?: string;
    validationState?: ValidationState;
    error?: string | null;
    variant?: 'default' | 'poll';
    isSelected?: boolean;
    clickable?: boolean;
    /** Optional trailing action button (e.g. password visibility toggle). Rendered to the left of validation icon. */
    trailingAction?: InputFieldTrailingAction;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
    ({ labelText, fieldRequired, fieldPlaceholder, validationState = 'none', error = null, className, variant = 'default', isSelected = false, clickable = false, trailingAction, ...props }, ref) => {
        const [isFocused, setIsFocused] = useState(false);
        const [inputLength, setInputLength] = useState(0);

        useEffect(() => {
            setInputLength(props.value ? String(props.value).length : 0);
        }, [props.value]);

        /** Derived from props to avoid stale state when label should return to "big" on blur with empty field. */
        const isEmpty = props.value === undefined || props.value === null || String(props.value).length === 0;

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const field = e.target as HTMLInputElement;
            setInputLength(field.value.length);
        };

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true);
            props.onFocus?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false);
            props.onBlur?.(e);
        };

        const isPollVariant = variant === 'poll';
        const canShowLabel = !isPollVariant && Boolean(labelText);
        const pollOutlineClass = isPollVariant && isSelected ? 'ring-2 ring-eoe-text-heading dark:ring-eoe-dark-text-heading' : '';
        const clickableClass = isPollVariant && clickable ? 'cursor-pointer' : 'cursor-text';

        const hasTrailingAction = Boolean(trailingAction);
        const hasValidationIcon = validationState !== 'none' && !isPollVariant;

        const PrimaryIcon = trailingAction?.primaryIcon;
        const SecondaryIcon = trailingAction?.secondaryIcon;

        return (
            <div className="relative">
                <label className="relative block">
                    <div className={`label-container ${clickableClass}`}>
                        <span className="input-boundary-box relative flex items-center">
                            <span className={`input-container relative flex-1 ${error ? 'error' : ''}`}>
                                <input
                                    ref={ref}
                                    placeholder={isFocused ? fieldPlaceholder : ''}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onInput={handleInputChange}
                                    className={`
                                        w-full eoe-input
                                        text-gray-800 dark:text-eoe-dark-text
                                        transition-colors
                                        resize-none ${clickableClass}
                                        ${pollOutlineClass}
                                        ${error ? 'text-warning-red dark:text-dark-warning-red' : ''}
                                        ${isPollVariant ? 'min-h-[44px] px-3 py-1 rounded-lg bg-eoe-bg dark:bg-eoe-dark-bg-modal border-none' : `min-h-[60px] pl-3 pt-6 pb-1 rounded-2xl bg-gray-200 dark:bg-eoe-dark-input ${hasTrailingAction ? 'pr-14' : 'pr-8'}`}
                                        ${className || ''}
                                    `}
                                    {...props}
                                />
                                {canShowLabel && (
                                    <span
                                        className={`
                                            absolute left-3
                                            ${isFocused ? 'top-2 text-xs' : isEmpty ? 'top-1/2 -translate-y-1/2' : 'top-2 text-xs'}
                                            transition-all
                                            ${error ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}
                                        `}
                                    >
                                        {labelText}
                                        {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                                    </span>
                                )}
                            </span>
                            {props.maxLength && !isPollVariant && (
                                <span className="absolute right-3 top-2 text-[10px] text-eoe-text-meta dark:text-eoe-dark-text-meta flex-shrink-0 place-items-end items-end text-right"><span className={`text-[10px] ${error || validationState === 'invalid' ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}`}>{inputLength}</span> / {props.maxLength}
                                </span>
                            )}

                            {(hasTrailingAction || hasValidationIcon) && !isPollVariant && (
                                <span className="input-field-trailing-actions absolute right-3 bottom-[20%] flex items-center flex-row-reverse">
                                    {hasValidationIcon && (
                                        <span className="input-field-validation-icon flex-shrink-0">
                                            {validationState === 'valid' && !error ? (
                                                <Check className="h-5 w-5 text-green-600" />
                                            ) : validationState === 'invalid' ? (
                                                <X className="h-5 w-5 text-warning-red dark:text-dark-warning-red" />
                                            ) : null}
                                        </span>
                                    )}
                                    {hasTrailingAction && PrimaryIcon && SecondaryIcon && (
                                        <button
                                            type="button"
                                            onClick={trailingAction.onToggle}
                                            aria-label={trailingAction.isPrimaryActive ? trailingAction.primaryLabel : trailingAction.secondaryLabel}
                                            className="input-field-trailing-action-btn p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text-heading dark:hover:text-eoe-dark-text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-eoe-text-heading dark:focus:ring-eoe-dark-text-heading"
                                        >
                                            {trailingAction.isPrimaryActive ? (
                                                <PrimaryIcon className="h-5 w-5" />
                                            ) : (
                                                <SecondaryIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    )}
                                </span>
                            )}
                        </span>
                    </div>
                </label>
                {error && (
                    <span className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">{error}</span>
                )}
            </div>
        );
    }
);

InputField.displayName = 'InputField';

export default InputField;